import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, History } from "lucide-react";

const RajwadaPalace = () => {
  const navigate = useNavigate();
  const palaceImage = "/Indore/historical/rajwadapalace0.jpg";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Rajwada Palace",
    "description": "Historic 7-story Maratha palace in Indore showcasing royal architecture and cultural heritage",
    "image": palaceImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Rajwada Chowk",
      "addressLocality": "Indore",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 09:00-18:00",
    "priceRange": "₹10",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.6",
      "reviewCount": "2500"
    }
  };

  const highlights = [
    {
      icon: <History className="w-6 h-6" />,
      title: "Royal Maratha Architecture",
      description: "7-story palace built by Holkar rulers showcasing traditional Maratha architectural style"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Wooden Carvings",
      description: "Intricate wooden balconies and carvings displaying exceptional craftsmanship"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Light & Sound Show",
      description: "Evening shows narrating the history of Holkar dynasty and Indore's heritage"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Central Location",
      description: "Located in the heart of old city, easily accessible and connected to other attractions"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "₹10 for Indians, ₹100 for Foreigners" },
    { label: "Timings", value: "9:00 AM - 6:00 PM (All days)" },
    { label: "Photography", value: "Allowed with restrictions" },
    { label: "Best Time", value: "October to March" },
    { label: "Duration", value: "1-2 hours" },
    { label: "Facilities", value: "Parking, Guides, Souvenir shop" }
  ];

  const nearbyAttractions = [
    { name: "Kanch Mandir", distance: "1.5 km", time: "5 mins" },
    { name: "Central Museum", distance: "2 km", time: "8 mins" },
    { name: "Sarafa Bazaar", distance: "0.5 km", time: "2 mins" },
    { name: "Khajuri Bazaar", distance: "0.8 km", time: "3 mins" }
  ];

  return (
    <>
      <Helmet>
        <title>Rajwada Palace Indore - Maratha Heritage & History | Aaoghumen</title>
        <meta name="description" content="Visit Rajwada Palace in Indore with Aaoghumen - explore 7-story Maratha palace, royal architecture, wooden carvings, and light & sound show. Complete guide with timings, entry fees, and nearby attractions." />
        <meta name="keywords" content="rajwada palace indore, rajwada palace history, indore palace, maratha architecture indore, rajwada timings, rajwada entry fee, aaoghumen rajwada, indore heritage, holkar palace indore" />
        
        <meta property="og:title" content="Rajwada Palace Indore - Royal Maratha Heritage | Aaoghumen" />
        <meta property="og:description" content="Discover the magnificent Rajwada Palace, a 7-story Maratha architectural marvel in the heart of Indore. Experience royal heritage and cultural history." />
                <meta name="twitter:image" content={palaceImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/rajwada-palace" />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/rajwada-palace" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-orange-600 via-red-500 to-yellow-600 overflow-hidden">
          <img
            src={palaceImage}
            alt="Rajwada Palace Indore"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-yellow-300">Rajwada Palace</span> - Indore
              </h1>
              <p className="text-xl mb-6">
                Step into the royal heritage of the Holkar dynasty at this magnificent 7-story Maratha palace, showcasing centuries of architectural brilliance and cultural legacy.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.6 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>9 AM - 6 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>₹10 Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Rajwada Palace</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Rajwada Palace stands as a magnificent testament to the architectural prowess and royal heritage of the Holkar dynasty in Indore. Built over 200 years ago, this seven-story palace combines Maratha, Mughal, and French architectural styles, creating a unique blend that reflects the cosmopolitan nature of the Holkar court. The palace's name "Rajwada" literally means "Royal Residence," and it served as the seat of power for the Holkar rulers who made Indore their capital.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The palace's most striking feature is its intricate wooden facade with beautiful balconies and carved pillars that showcase exceptional craftsmanship. Each floor tells a different story of royal life, from the ground floor's grand darbar hall to the upper floors' private quarters. The palace has been meticulously restored to preserve its original grandeur while incorporating modern amenities for visitors.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, explore the palace's rich history through guided tours, witness the mesmerizing light and sound show that brings the Holkar legacy to life, and understand the significant role this palace played in shaping Indore's cultural identity. The palace remains a symbol of Indore's glorious past and continues to be a source of pride for the city.
              </p>
            </div>
          </section>

          {/* Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Palace Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-orange-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-orange-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Things to Do */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">What to Experience</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Architectural Photography</h3>
                <p className="text-gray-600 mb-4">Capture the intricate wooden carvings, beautiful balconies, and architectural details that make Rajwada unique.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Best light: Morning and evening</li>
                  <li>• Focus on wooden facades</li>
                  <li>• Interior courtyards</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Light & Sound Show</h3>
                <p className="text-gray-600 mb-4">Experience the palace's history through an immersive multimedia presentation showcasing Holkar dynasty's legacy.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Evening shows available</li>
                  <li>• Multiple language options</li>
                  <li>• 45-minute duration</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Cultural Exploration</h3>
                <p className="text-gray-600 mb-4">Learn about Maratha culture, royal lifestyle, and the historical significance of the Holkar dynasty.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Guided tours available</li>
                  <li>• Historical artifacts</li>
                  <li>• Royal memorabilia</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-orange-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus:</strong> City buses to Rajwada Chowk, major bus route hub</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Auto:</strong> Available from all parts of city, ₹30-80 depending on distance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Taxi:</strong> Uber/Ola readily available, central location</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Limited street parking, paid parking available nearby</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Location:</strong> Central Indore, well-connected roads</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Navigation:</strong> Search "Rajwada Palace" on Google Maps</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Plan Your Visit to Rajwada Palace</h2>
              <p className="text-xl mb-6">Experience the royal heritage of Indore with expert guides and local insights</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Rajwada Chowk, Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-orange-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Book Guided Tour
                </button>
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-orange-600 transition-colors"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default RajwadaPalace;